import type { ReactNode } from 'react'
import { Box, Flex, Text, Dialog, Portal } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { LuCircleCheck, LuTrash2, LuTriangleAlert } from 'react-icons/lu'
import { FONT_DISPLAY, INK, INK_MUTED, LINE, SHADOW_LG } from '../tokens'

interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  children: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'danger' | 'warning' | 'default'
  isLoading?: boolean
}

const VARIANTS = {
  danger: { bg: '#c8442f', soft: '#fae1db', Icon: LuTrash2 },
  warning: { bg: '#a8740e', soft: '#f9eccf', Icon: LuTriangleAlert },
  default: { bg: '#b4532a', soft: '#f9e6da', Icon: LuCircleCheck },
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  children,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  variant = 'default',
  isLoading,
}: ConfirmDialogProps) {
  const v = VARIANTS[variant]

  return (
    <Dialog.Root open={open} onOpenChange={(e) => { if (!e.open) onClose() }}>
      <Portal>
        <Dialog.Backdrop bg="rgba(42, 30, 20, 0.42)" backdropFilter="blur(4px)" />
        <Dialog.Positioner>
          <Dialog.Content bg="white" borderRadius="20px" p={6} maxW="420px" boxShadow={SHADOW_LG} border={`1px solid ${LINE}`}>
            <Flex gap={4} align="flex-start">
              {/* El ícono anticipa la consecuencia: un borrado "tiembla" antes de que lo confirmes. */}
              <motion.div
                initial={{ scale: 0.4, rotate: -20, opacity: 0 }}
                animate={variant === 'danger'
                  ? { scale: 1, rotate: [0, -8, 8, -4, 0], opacity: 1 }
                  : { scale: 1, rotate: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.05 }}
                style={{ flexShrink: 0 }}
              >
                <Flex w="44px" h="44px" borderRadius="14px" align="center" justify="center" bg={v.soft} color={v.bg}>
                  <v.Icon size={21} />
                </Flex>
              </motion.div>
              <Box flex={1}>
                <Dialog.Title fontFamily={FONT_DISPLAY} fontSize="18px" fontWeight="700" color={INK} letterSpacing="-0.02em">
                  {title}
                </Dialog.Title>
                <Text as="div" fontSize="14px" color={INK_MUTED} mt={1.5} lineHeight="1.55">
                  {children}
                </Text>
              </Box>
            </Flex>
            <Flex mt={6} gap={2.5} justify="flex-end">
              {/* cancelLabel="" = diálogo informativo de un solo botón. */}
              {cancelLabel && (
                <Box
                  as="button" {...{ type: 'button' }}
                  px={4} h="40px" borderRadius="11px" fontSize="14px" fontWeight="600"
                  border={`1px solid ${LINE}`} color={INK} cursor="pointer" _hover={{ bg: '#f3f1ed' }}
                  onClick={onClose}
                >
                  {cancelLabel}
                </Box>
              )}
              <motion.button
                type="button"
                whileTap={isLoading ? undefined : { scale: 0.96 }}
                onClick={onConfirm}
                aria-disabled={isLoading}
                style={{
                  padding: '0 18px', height: 40, borderRadius: 11, fontSize: 14, fontWeight: 650, border: 0, color: '#fff',
                  background: v.bg, cursor: isLoading ? 'wait' : 'pointer', opacity: isLoading ? 0.7 : 1, position: 'relative', overflow: 'hidden',
                  boxShadow: `0 8px 18px -10px ${v.bg}`,
                }}
              >
                {isLoading && (
                  <motion.span animate={{ x: ['-100%', '220%'] }} transition={{ duration: 1, repeat: Infinity }}
                    style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: '50%', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)' }} />
                )}
                <span style={{ position: 'relative' }}>{confirmLabel}</span>
              </motion.button>
            </Flex>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
